/**
 * SmartRoutingDashboard - AI Routing Intelligence Dashboard
 * Shows routing efficiency, driver performance, PUDO utilization, and route optimization
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  ArrowLeft, 
  Route, 
  Zap, 
  Clock, 
  Users, 
  MapPin, 
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ChevronRight,
  Target,
  Truck,
  Package,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Map,
  Settings,
  Brain,
  Loader2
} from 'lucide-react';

// Efficiency status colors
const EFFICIENCY_COLORS = {
  excellent: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
  good: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  moderate: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  poor: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
};

const getEfficiencyLevel = (score) => {
  if (score >= 85) return 'excellent';
  if (score >= 70) return 'good';
  if (score >= 50) return 'moderate';
  return 'poor';
};

export default function SmartRoutingDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [efficiency, setEfficiency] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [routeAnalysis, setRouteAnalysis] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [effRes, heatRes] = await Promise.all([
        api.get('/routing/analytics/efficiency?days=7'),
        api.get('/routing/analytics/heatmap')
      ]);
      
      setEfficiency(effRes.data);
      setHeatmapData(heatRes.data);
    } catch (error) {
      console.error('Error fetching routing data:', error);
      toast.error('Failed to load routing data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const analyzeRoute = async () => {
    try {
      // Demo route: Kingston to Montego Bay
      const res = await api.get('/routing/analyze', {
        params: {
          pickup_lat: 17.9714,
          pickup_lng: -76.7936,
          dropoff_lat: 18.4667,
          dropoff_lng: -77.9167
        }
      });
      setRouteAnalysis(res.data);
      toast.success('Route analyzed successfully');
    } catch (error) {
      toast.error('Failed to analyze route');
    }
  };

  const effLevel = efficiency ? getEfficiencyLevel(efficiency.efficiency_score) : 'moderate';
  const effColors = EFFICIENCY_COLORS[effLevel];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading routing intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Brain className="w-5 h-5 text-green-500" />
                  Smart Routing Intelligence
                </h1>
                <p className="text-sm text-slate-400">
                  AI-powered routing optimization & analytics
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="border-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'efficiency', label: 'Efficiency', icon: Zap },
            { id: 'regions', label: 'Regions', icon: Map },
            { id: 'analyze', label: 'Route Analyzer', icon: Route },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'border-slate-700 text-slate-300'
                }
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className={`p-4 ${effColors.bg} ${effColors.border} border`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${effColors.bg} flex items-center justify-center`}>
                    <Zap className={`w-5 h-5 ${effColors.text}`} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Efficiency Score</p>
                    <p className={`text-2xl font-bold ${effColors.text}`}>
                      {efficiency?.efficiency_score || 0}%
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Total Packages</p>
                    <p className="text-2xl font-bold text-white">
                      {efficiency?.total_packages || 0}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                    <Route className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Chain Deliveries</p>
                    <p className="text-2xl font-bold text-white">
                      {efficiency?.chain_deliveries || 0}
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Delivery Rate</p>
                    <p className="text-2xl font-bold text-white">
                      {efficiency?.delivery_rate || 0}%
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Recommendations */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-400" />
                AI Recommendations
              </h3>
              <div className="space-y-3">
                {efficiency?.recommendations?.map((rec, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-slate-900/50 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                    <p className="text-sm text-slate-300">{rec}</p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Regional Performance */}
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-400" />
                Regional Performance
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {heatmapData?.regions?.map((region, i) => {
                  const level = getEfficiencyLevel(region.efficiency);
                  const colors = EFFICIENCY_COLORS[level];
                  return (
                    <div key={i} className={`p-4 rounded-lg ${colors.bg} ${colors.border} border`}>
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-white">{region.name}</h4>
                        <Badge className={`${colors.bg} ${colors.text}`}>
                          {region.efficiency}%
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-400">
                        {region.deliveries} deliveries
                      </p>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Efficiency Tab */}
        {activeTab === 'efficiency' && (
          <div className="space-y-6">
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-green-400" />
                Efficiency Metrics
              </h3>
              
              <div className="space-y-4">
                {/* Overall Efficiency */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-400">Overall Efficiency</span>
                    <span className="text-sm font-medium text-white">{efficiency?.efficiency_score || 0}%</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-600 to-green-400 rounded-full transition-all"
                      style={{ width: `${efficiency?.efficiency_score || 0}%` }}
                    />
                  </div>
                </div>

                {/* Chain Usage */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-400">Chain Delivery Usage</span>
                    <span className="text-sm font-medium text-white">{efficiency?.chain_percentage || 0}%</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full transition-all"
                      style={{ width: `${efficiency?.chain_percentage || 0}%` }}
                    />
                  </div>
                </div>

                {/* Delivery Rate */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-slate-400">Successful Delivery Rate</span>
                    <span className="text-sm font-medium text-white">{efficiency?.delivery_rate || 0}%</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all"
                      style={{ width: `${efficiency?.delivery_rate || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <p className="text-xs text-slate-400 mb-1">Avg Assignment Time</p>
                <p className="text-xl font-bold text-white flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-400" />
                  {efficiency?.avg_assignment_minutes || 0} min
                </p>
              </Card>
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <p className="text-xs text-slate-400 mb-1">Period Analyzed</p>
                <p className="text-xl font-bold text-white flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  {efficiency?.period_days || 0} days
                </p>
              </Card>
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <p className="text-xs text-slate-400 mb-1">Total Regions</p>
                <p className="text-xl font-bold text-white flex items-center gap-2">
                  <Map className="w-4 h-4 text-purple-400" />
                  {heatmapData?.regions?.length || 0}
                </p>
              </Card>
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <p className="text-xs text-slate-400 mb-1">Avg Regional Efficiency</p>
                <p className="text-xl font-bold text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  {heatmapData?.avg_efficiency || 0}%
                </p>
              </Card>
            </div>
          </div>
        )}

        {/* Regions Tab */}
        {activeTab === 'regions' && (
          <div className="space-y-6">
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Map className="w-5 h-5 text-green-400" />
                Regional Breakdown
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Region</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Deliveries</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Efficiency</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapData?.regions?.map((region, i) => {
                      const level = getEfficiencyLevel(region.efficiency);
                      const colors = EFFICIENCY_COLORS[level];
                      return (
                        <tr key={i} className="border-b border-slate-800 hover:bg-slate-800/30">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span className="font-medium text-white">{region.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center text-slate-300">
                            {region.deliveries}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`font-medium ${colors.text}`}>{region.efficiency}%</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={`${colors.bg} ${colors.text}`}>
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* Route Analyzer Tab */}
        {activeTab === 'analyze' && (
          <div className="space-y-6">
            <Card className="p-6 bg-slate-800/50 border-slate-700">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Route className="w-5 h-5 text-green-400" />
                Route Analyzer
              </h3>
              
              <p className="text-sm text-slate-400 mb-4">
                Analyze a route to determine optimal delivery strategy, chain requirements, and ETA estimates.
              </p>
              
              <Button
                onClick={analyzeRoute}
                className="bg-green-600 hover:bg-green-700"
              >
                <Brain className="w-4 h-4 mr-2" />
                Analyze Demo Route (Kingston → Montego Bay)
              </Button>
              
              {routeAnalysis && (
                <div className="mt-6 space-y-4">
                  {/* Complexity */}
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Route Complexity</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-slate-400">Complexity Score</p>
                        <p className="text-lg font-bold text-yellow-400">{routeAnalysis.complexity?.complexity_score}/10</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Distance</p>
                        <p className="text-lg font-bold text-white">{routeAnalysis.complexity?.distance_km} km</p>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <p className="text-xs text-slate-400 mb-2">Factors</p>
                      <div className="flex flex-wrap gap-2">
                        {routeAnalysis.complexity?.factors?.map((factor, i) => (
                          <Badge key={i} className="bg-slate-700 text-slate-300">
                            {factor.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Travel Estimate */}
                  <div className="p-4 bg-slate-900/50 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Travel Estimate</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-slate-400">Estimated Time</p>
                        <p className="text-lg font-bold text-green-400">{routeAnalysis.travel?.estimated_minutes} min</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Base Speed</p>
                        <p className="text-lg font-bold text-white">{routeAnalysis.travel?.base_speed_kmh} km/h</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Effective Speed</p>
                        <p className="text-lg font-bold text-white">{routeAnalysis.travel?.effective_speed_kmh} km/h</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Traffic Period</p>
                        <p className="text-lg font-bold text-blue-400">{routeAnalysis.travel?.traffic_period?.replace(/_/g, ' ')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Recommendation */}
                  <div className={`p-4 rounded-lg ${routeAnalysis.recommendation?.use_chain ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
                    <h4 className="font-medium text-white mb-2">AI Recommendation</h4>
                    <div className="flex items-center gap-3">
                      {routeAnalysis.recommendation?.use_chain ? (
                        <>
                          <Route className="w-8 h-8 text-purple-400" />
                          <div>
                            <p className="font-medium text-purple-400">Chain Delivery Recommended</p>
                            <p className="text-sm text-slate-300">
                              Split into {routeAnalysis.recommendation?.recommended_legs} legs for optimal efficiency
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Truck className="w-8 h-8 text-green-400" />
                          <div>
                            <p className="font-medium text-green-400">Direct Delivery Optimal</p>
                            <p className="text-sm text-slate-300">
                              Single driver can complete this route efficiently
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
